import { Button, Datepicker, Modal, MODAL_FADE_TIMEOUT, Space, Spinner, Typography } from '@ag1/ui';
import { routes } from 'constants/routes';
import { addMonths, startOfToday, startOfTomorrow } from 'date-fns';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import { Matcher, OnSelectHandler } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useDate } from 'shared/hooks';
import { getDurationForSubscription } from 'shared/utils/getDurationForSubscription';
import { useActivateSubscriptionV2 } from 'state/hooks/useActivateSubscriptionV2';
import { useQueryGetSubscriptionDetailsV2 } from 'state/internal/getSubscriptionDetails';
import { useMutationUpdateSubscriptionDetailsV2 } from 'state/internal/updateSubscriptionDetails';
import { showNotification } from '../../ui/Notification';
import ModalConfirmationScreenContent from '../ModalConfirmationScreen/ModalConfirmationScreenContent';
import { ActivateSubscriptionModalDatepickerInfo } from './ActivateSubscriptionModalDatepickerInfoV2/ActivateSubscriptionModalDatepickerInfo';
import styles from './styles.module.scss';
import { ActivateSubscriptionConfirmation } from './types';
import { useActivateSubscriptionModalV2 } from './useActivateSubscriptionModalV2';

const ActivateSubscriptionModalV2 = () => {
  const { t } = useTranslation('modalWindows');
  const router = useRouter();
  const { isOpen, subscriptionId, handleClose } = useActivateSubscriptionModalV2();

  const { activateSubscription, isPending: isActivateSubscriptionLoading } =
    useActivateSubscriptionV2({
      subscriptionId
    });

  const { data: subscriptionDetails } = useQueryGetSubscriptionDetailsV2(subscriptionId || 0);
  const {
    frequency_interval: period,
    frequency_count: interval,
    custom_name: name
  } = subscriptionDetails || {};
  const { mutate: updateSubscription, isPending: isMutateQueueLoading } =
    useMutationUpdateSubscriptionDetailsV2();

  const isLoading = isActivateSubscriptionLoading || isMutateQueueLoading;

  const modalTitle = t('activateSubscription.title');

  const startOfTomorrowDate = startOfTomorrow();
  const startOfTodayDate = startOfToday();

  const [selectedDate, setSelectedDate] = useState(startOfTomorrowDate);

  const disabledDates: Matcher[] = [
    selectedDate || false,
    { before: startOfTomorrowDate, after: addMonths(startOfTodayDate, 1) }
  ];

  const [confirmation, setConfirmation] = useState<ActivateSubscriptionConfirmation>(null);
  const [isSending, setIsSending] = useState(false);
  const durationForSubscription = useMemo(
    () => (interval && period ? getDurationForSubscription(interval, period) : null),
    [interval, period]
  );

  const { formattedDateString: nextOrderDate } = useDate({
    date: selectedDate,
    dateFormat: 'yyyy-MM-dd'
  });
  const { formattedDateString: nextOrderDateLong } = useDate({
    date: selectedDate,
    dateFormat: 'MMMM d, yyyy'
  });

  const handleReset = useCallback(() => {
    setSelectedDate(startOfTomorrowDate);
    setConfirmation(null);
  }, [startOfTomorrowDate]);

  const handleCloseInternal = useCallback(() => {
    handleClose();
    setTimeout(() => {
      handleReset();
    }, MODAL_FADE_TIMEOUT);
  }, [handleClose, handleReset]);

  const handleSelectDate: OnSelectHandler<Date> = useCallback((date) => {
    if (date) {
      setSelectedDate(date);
    }
    return date;
  }, []);

  const handleActivateSubscription = useCallback(async () => {
    if (!subscriptionId || !nextOrderDate) {
      showNotification('mutationError', { title: t('activateSubscription.errorMessage') });
      return;
    }
    activateSubscription({
      onSuccess: () => {
        const data = {
          next_order_date: nextOrderDate
        };

        setIsSending(true);
        setTimeout(() => {
          updateSubscription(
            { contractId: subscriptionId, data, isReactivate: true },
            {
              onSuccess: () => {
                setIsSending(false);
                setConfirmation({
                  subtitle: t('activateSubscription.successTitle', { name }),
                  description: t('activateSubscription.successDescription', {
                    date: nextOrderDateLong
                  })
                });
              },
              onError: () => {
                setIsSending(false);
                handleClose();
                showNotification('mutationError', {
                  title: t('activateSubscription.errorMessage')
                });
              }
            }
          );
        }, 3000);
      }
    });
  }, [
    subscriptionId,
    nextOrderDate,
    nextOrderDateLong,
    name,
    handleClose,
    updateSubscription,
    activateSubscription,
    t
  ]);

  const handleConfirmationCtaClick = useCallback(() => {
    handleClose();

    setTimeout(() => {
      handleReset();

      const isHome = routes.home === router.pathname;

      if (isHome && subscriptionId) {
        router.push(routes.subscriptionDetail({ id: subscriptionId.toString() }));
      }
    }, MODAL_FADE_TIMEOUT);
  }, [handleClose, handleReset, router, subscriptionId]);

  return (
    <Modal isOpen={isOpen} onClose={handleCloseInternal}>
      {confirmation ? (
        <ModalConfirmationScreenContent
          title={modalTitle}
          subtitle={confirmation.subtitle}
          description={confirmation.description}
          ctaOnClick={handleConfirmationCtaClick}
        />
      ) : (
        <>
          <Modal.Header>{modalTitle}</Modal.Header>
          <Modal.Content hasFooter={!confirmation}>
            <Typography noPadding variant="portalText" size="1" color="black-new">
              {t('activateSubscription.infoMessage')}
            </Typography>
            <Space top={{ xxs: 16, s: 24 }}>
              <Datepicker
                required
                mode="single"
                selected={selectedDate}
                onSelect={handleSelectDate}
                disabled={disabledDates}
              />
            </Space>
            <Space top={{ xxs: 12, s: 16 }}>
              <ActivateSubscriptionModalDatepickerInfo
                value={selectedDate}
                durationForSubscription={durationForSubscription}
              />
            </Space>
          </Modal.Content>
          <Modal.Footer>
            <Button
              variant="primary"
              theme="new"
              size={{ xxs: 'small', s: 'medium' }}
              disabled={isLoading}
              onClick={handleActivateSubscription}
              fluid
              className={styles.btn}>
              {isLoading || isSending ? (
                <Spinner color="brand-white" size={24} withOverlay={false} />
              ) : (
                t('activateSubscription.activateButton')
              )}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default ActivateSubscriptionModalV2;
